import { Head, router } from "@inertiajs/react"
import { useState } from "react"

import { HpiFilterSelect, HpiStatsBar } from "@/components/hpi"
import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

/* ── Types ──────────────────────────────────────────────── */

interface ExpenseRow {
  id: number
  name: string
  amounts: Record<number, number>
}

interface Company {
  id: number
  name: string
}

interface Props {
  rows: ExpenseRow[]
  years: number[]
  totals: Record<number, number>
  companies: Company[]
  selected_company_id: number | null
  company_name: string | null
}

/* ── Helpers ────────────────────────────────────────────── */

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(amount)
}

/* ── Main Component ────────────────────────────────────── */

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Reports", href: "/reports/expenses_by_type" },
  { title: "Expenses by Type", href: "/reports/expenses_by_type" },
]

export default function ExpensesByTypeReport({
  rows,
  years,
  totals,
  companies,
  selected_company_id,
  company_name,
}: Props) {
  const [companyId, setCompanyId] = useState<string>(
    selected_company_id?.toString() ?? "",
  )

  const handleUpdate = () => {
    const params: Record<string, string> = {}
    if (companyId) params.company_id = companyId
    router.get("/reports/expenses_by_type", params, { preserveState: true })
  }

  const grandTotal = Object.values(totals).reduce((sum, v) => sum + v, 0)

  const title = company_name
    ? `Expenses by Type — ${company_name}`
    : "Expenses by Type"

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={title} />

      <div className="p-6">
        {/* Company Filter */}
        <div className="border-hpi-light-gray mb-6 flex items-end gap-4 rounded-lg border bg-white p-4">
          <HpiFilterSelect
            label="Company"
            value={companyId}
            onChange={setCompanyId}
            options={companies.map((c) => ({ value: c.id, label: c.name }))}
            placeholder="All Companies"
          />
          <button
            onClick={handleUpdate}
            className="bg-hpi-blue hover:bg-hpi-dark-blue h-10 rounded-md px-4 text-sm font-medium text-white transition-colors"
          >
            Update
          </button>
        </div>

        <HpiStatsBar
          stats={[
            {
              label: "Expense",
              subLabel: "Types",
              value: rows.length,
            },
            {
              label: "Grand",
              subLabel: "Total",
              value: formatCurrency(grandTotal),
            },
          ]}
          className="mb-6"
        />

        {/* Cross-tab Matrix: expense types (rows) x years (columns) */}
        <div className="border-hpi-light-gray overflow-x-auto rounded-lg border bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-hpi-light-gray border-b bg-gray-50">
                <th className="text-hpi-dark-gray sticky left-0 bg-gray-50 px-4 py-3 text-left font-semibold">
                  Expense Type
                </th>
                {years.map((year) => (
                  <th
                    key={year}
                    className="text-hpi-dark-gray px-4 py-3 text-right font-semibold whitespace-nowrap"
                  >
                    {year}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.id}
                  className="border-hpi-light-gray border-b hover:bg-gray-50"
                >
                  <td className="text-hpi-dark-gray sticky left-0 bg-white px-4 py-2 font-medium">
                    {row.name}
                  </td>
                  {years.map((year) => {
                    const amount = row.amounts[year] ?? 0
                    return (
                      <td key={year} className="px-4 py-2 text-right">
                        {amount > 0 ? (
                          <a
                            href={`/accounting/reports/expenses?expense_type_id=${row.id}&start_date=${year}-01-01&end_date=${year}-12-31`}
                            className="text-hpi-active-blue hover:underline"
                          >
                            {formatCurrency(amount)}
                          </a>
                        ) : (
                          <span className="text-hpi-med-gray">-</span>
                        )}
                      </td>
                    )
                  })}
                </tr>
              ))}
              {rows.length > 0 && (
                <tr className="border-hpi-dark-gray border-t-2 bg-gray-50 font-bold">
                  <td className="text-hpi-dark-gray sticky left-0 bg-gray-50 px-4 py-2">
                    TOTAL
                  </td>
                  {years.map((year) => (
                    <td key={year} className="px-4 py-2 text-right">
                      {formatCurrency(totals[year] ?? 0)}
                    </td>
                  ))}
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {rows.length === 0 && (
          <div className="border-hpi-light-gray text-hpi-med-blue mt-4 rounded-lg border bg-white p-8 text-center">
            No expense data found for the selected criteria.
          </div>
        )}
      </div>
    </AppLayout>
  )
}
